"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { ECON_CHART_STRINGS } from "@/components/tools/econ-micro/chart-strings";
import { CostGraph } from "@/components/tools/econ-micro/cost-graph";
import { CostView } from "@/components/tools/econ-micro/cost-view";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { parseCell, parseLabeledRows } from "@/lib/tool-kit/charts/parse";
import {
  buildCostTable,
  type CostError,
  type CostPoint,
} from "@/lib/tool-kit/econ-micro/costs";

/** English strings. A translated content file overrides these through `ui`. */
export const COST_CURVES_CALCULATOR_STRINGS = {
  ...ECON_CHART_STRINGS,
  dataLabel: "Output and cost — one pair per line: Q, cost",
  dataHint:
    "Separate the two numbers with a comma, tab, semicolon or space (use semicolons if your numbers have decimal commas). A first line without numbers is read as a header. Output can be listed in any order and in steps of any size.",
  exampleData:
    "Output, Total cost\n0, 100\n1, 125\n2, 144\n3, 163\n4, 188\n5, 225\n6, 280\n7, 359\n8, 468\n9, 613\n10, 800",
  costKindLabel: "The cost column is",
  costKindTotal: "Total cost (TC)",
  costKindVariable: "Variable cost (VC)",
  fixedLabel: "Fixed cost (FC)",
  fixedHint:
    "Needed when the cost column is variable cost, or when there is no Q = 0 row. With total cost data, a Q = 0 row sets the fixed cost.",
  priceLabel: "Market price (optional)",
  priceHint:
    "The price a competitive firm takes as given. Fill it in for the profit-maximising output and the shut-down decision.",
  showAfc: "Show the AFC curve",
  results: "Results",
  noRows: "Enter at least one line with an output and a cost.",
  duplicateOutput: "Each output can appear only once.",
  negative: "Output and cost cannot be negative.",
  needFixedCost:
    "Enter the fixed cost: with no Q = 0 row in total cost data, there is nothing to read it from.",
  costBelowFixed:
    "Total cost cannot be less than fixed cost at a positive output. Check the cost column.",
  noPositiveOutput: "Add at least one row with an output above zero.",
  badPrice: "The market price must be a number of 0 or more.",
  skippedLines:
    "one:Line {lines} has no number and was left out.|other:Lines {lines} have no numbers and were left out.",
  fallingCost:
    "Total cost falls as output rises at Q = {outputs}, so marginal cost is negative there. Check the data.",
  statFixed: "Fixed cost (FC)",
  statBreakEven: "Break-even price: minimum ATC, at Q = {q}",
  statShutdown: "Shutdown price: minimum AVC, at Q = {q}",
  thresholdNote:
    "In the short run a competitive firm earns zero economic profit when price equals the lowest ATC, and stops producing when price falls below the lowest AVC. Both are read from the outputs you listed.",
  tableHeading: "Cost table",
  columnQ: "Q",
  columnTc: "TC",
  columnFc: "FC",
  columnVc: "VC",
  columnAfc: "AFC",
  columnAvc: "AVC",
  columnAtc: "ATC",
  columnMc: "MC",
  columnProfit: "Profit",
  tableNote:
    "VC = TC − FC. AFC, AVC and ATC divide by Q. MC = ΔTC ÷ ΔQ, measured from the row above. Profit = P × Q − TC at the market price.",
  priceHeading: "At a market price of {price}",
  statBestOutput: "Profit-maximising output",
  statRevenue: "Total revenue",
  statProfit: "Profit (loss if negative)",
  decisionProfit:
    "Produce: price is above the minimum ATC, so the firm earns an economic profit.",
  decisionBreakEven:
    "Produce: price equals the minimum ATC, so the firm earns zero economic profit.",
  decisionLoss:
    "Produce at a loss: price is above the minimum AVC but below the minimum ATC, so revenue covers variable cost and part of the fixed cost. The loss is smaller than the fixed cost the firm owes anyway.",
  decisionShutDown:
    "Shut down: price is below the minimum AVC, so every unit loses money. Producing nothing loses only the fixed cost.",
  ruleNote:
    "The output is the listed quantity with the highest profit, P × Q − TC. With rising marginal cost that is the last unit whose MC is no higher than the price.",
  graphTitle: "Cost curves",
  yAxis: "Cost per unit",
  xAxis: "Quantity (Q)",
  curveMc: "MC",
  curveAtc: "ATC",
  curveAvc: "AVC",
  curveAfc: "AFC",
  curvePrice: "P",
  pointBreakEven: "Break-even",
  pointShutdown: "Shutdown",
  graphNote:
    "MC is plotted halfway between the two outputs it is measured over. Curves are straight lines between the listed outputs, and a steep start or tail is cut off so the minimum stays readable.",
  graphDescription:
    "Cost curves with cost per unit on the vertical axis and quantity on the horizontal axis. Average total cost reaches its minimum of {atc} at a quantity of {atcQ}; average variable cost reaches its minimum of {avc} at a quantity of {avcQ}. Marginal cost crosses both near their minimums.",
} as const;

export type CostCurvesStrings = typeof COST_CURVES_CALCULATOR_STRINGS;
type Kind = "tc" | "vc";

function toPoints(raw: string) {
  const parsed = parseLabeledRows(raw);
  const points: CostPoint[] = [];
  const skipped = [...parsed.skipped];
  for (const row of parsed.rows) {
    const q = parseCell(row.label);
    const cost = row.values[0];
    if (q === null || cost === null || cost === undefined) {
      skipped.push(row.line);
    } else {
      points.push({ q, cost });
    }
  }
  return { points, skipped: skipped.sort((a, b) => a - b) };
}

export default function CostCurvesCalculatorWidget() {
  const strings = useToolUi(COST_CURVES_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [kind, setKind] = useState<Kind>("tc");
  const [fixed, setFixed] = useState("");
  const [priceText, setPriceText] = useState("40");
  const [showAfc, setShowAfc] = useState(false);

  const { points, skipped } = toPoints(raw);
  const price = priceText.trim() === "" ? null : parseNumberInput(priceText);
  const priceBad = priceText.trim() !== "" && (price === null || price < 0);
  const fixedValue = fixed.trim() === "" ? null : parseNumberInput(fixed);
  const built = buildCostTable(points, kind, fixedValue);
  const errors: Record<CostError, string> = {
    "no-rows": strings.noRows,
    "duplicate-output": strings.duplicateOutput,
    negative: strings.negative,
    "need-fixed-cost": strings.needFixedCost,
    "cost-below-fixed": strings.costBelowFixed,
    "no-positive-output": strings.noPositiveOutput,
  };
  const table = typeof built === "string" ? null : built;
  const usablePrice = priceBad ? null : price;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="cc-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={8}
        value={raw}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="cc-kind"
          label={strings.costKindLabel}
          onChange={setKind}
          options={[
            { value: "tc", label: strings.costKindTotal },
            { value: "vc", label: strings.costKindVariable },
          ]}
          value={kind}
        />
        <TextField
          hint={strings.fixedHint}
          id="cc-fixed"
          label={strings.fixedLabel}
          numeric
          onChange={setFixed}
          value={fixed}
        />
        <TextField
          hint={strings.priceHint}
          id="cc-price"
          label={strings.priceLabel}
          numeric
          onChange={setPriceText}
          value={priceText}
        />
      </div>
      <Checkbox
        checked={showAfc}
        label={strings.showAfc}
        onChange={setShowAfc}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typeof built === "string" ? (
          <ErrorNotice>{errors[built]}</ErrorNotice>
        ) : null}
        {priceBad ? <ErrorNotice>{strings.badPrice}</ErrorNotice> : null}
        {skipped.length > 0 ? (
          <Notice>
            {tPlural(strings.skippedLines, locale, skipped.length, {
              lines: skipped.map((n) => fmt(n)).join(", "),
            })}
          </Notice>
        ) : null}
        {table && table.fallingCost.length > 0 ? (
          <Notice>
            {t(strings.fallingCost, {
              outputs: table.fallingCost.map((q) => fmt(q)).join(", "),
            })}
          </Notice>
        ) : null}
        {table ? (
          <>
            <CostView
              fmt={fmt}
              price={usablePrice}
              strings={strings}
              table={table}
            />
            <CostGraph
              fmt={fmt}
              price={usablePrice}
              showAfc={showAfc}
              strings={strings}
              table={table}
            />
          </>
        ) : null}
      </section>
    </div>
  );
}
